/**
 * Which lot to suggest (spec §8, §9): first expiring, first out. An expired lot, or a bottle past its
 * in-use window, is never suggested but stays selectable behind a warning; nothing here blocks a save.
 * Expiry is a calendar day and "today" is the clinic's (DATE-KINDS §2.9), so every count goes through
 * `calendarDaysUntil` with the clinic zone.
 */
import type { InventoryLot } from '../../services/inventoryService';
import { calendarDaysUntil, formatInstantInZone } from '../../utils/dateUtils';
import { drawMlFor } from './lotDraw';

export const EXPIRING_SOON_DAYS = 60;

export type LotExpiry = 'ok' | 'expiring' | 'expired';

export function lotExpiry(lot: Pick<InventoryLot, 'expiresOn'>, tz: string, now: number = Date.now()): LotExpiry {
  const days = calendarDaysUntil(lot.expiresOn, tz, now);
  if (days === null) return 'ok';
  if (days < 0) return 'expired';
  return days <= EXPIRING_SOON_DAYS ? 'expiring' : 'ok';
}

/** Whole days the open bottle has been open, counted in the clinic's days; null when none is open. */
export function daysOpen(lot: Pick<InventoryLot, 'openedAt'>, tz: string, now: number = Date.now()): number | null {
  if (!lot.openedAt) return null;
  const openedDay = formatInstantInZone(lot.openedAt, tz, 'yyyy-MM-dd');
  const until = calendarDaysUntil(openedDay, tz, now);
  return until === null ? null : Math.max(0, -until);
}

/** Past a RECORDED in-use window. An unknown window (the usual case) never gates anything. */
export function isPastInUseWindow(lot: Pick<InventoryLot, 'openedAt' | 'inUseDays'>, tz: string, now: number = Date.now()): boolean {
  const open = daysOpen(lot, tz, now);
  return open !== null && lot.inUseDays != null && open > lot.inUseDays;
}

/** The lot to suggest: earliest expiry that is in date, in its window and not empty. */
export function suggestLot(lots: InventoryLot[], tz: string, now: number = Date.now()): InventoryLot | null {
  return (
    [...lots]
      .sort((a, b) => a.expiresOn.localeCompare(b.expiresOn))
      .find((l) => l.mlRemaining > 0 && lotExpiry(l, tz, now) !== 'expired' && !isPastInUseWindow(l, tz, now)) ?? null
  );
}

export interface LotAllocation {
  lot: InventoryLot | null;
  /** No lot had a whole dose left: the horse stays on the last lot, which the server flags LOT_SHORT. */
  short: boolean;
}

/**
 * A barn run's doses, one per horse in order (spec §18): the lot the vet picked first (`first`, expired or not),
 * then the usable lots earliest expiry first, moving on when a lot cannot give a whole dose. Single-dose lots
 * give whole vials. A horse nothing covers stays on the last lot rather than none, because a lot-tracked dose
 * with no lot draws whole containers (INVR256). Without a dose there is nothing to spill: all take the first lot.
 */
export function allocateLots(
  lots: InventoryLot[],
  count: number,
  doseMl: number | null,
  tz: string,
  first?: string,
  now: number = Date.now(),
): LotAllocation[] {
  const usable = [...lots]
    .sort((a, b) => a.expiresOn.localeCompare(b.expiresOn))
    .filter((l) => l.mlRemaining > 0 && lotExpiry(l, tz, now) !== 'expired' && !isPastInUseWindow(l, tz, now));
  const picked = first ? lots.find((l) => l.lotNumber === first) : undefined;
  const order = picked ? [picked, ...usable.filter((l) => l.id !== picked.id)] : usable;
  if (order.length === 0 || !doseMl || doseMl <= 0) {
    return Array.from({ length: count }, () => ({ lot: order[0] ?? null, short: false }));
  }
  // Whole thousandths of a mL, the stock grid, so float noise never moves a horse to the next lot.
  const left = new Map(order.map((l) => [l.id, Math.round(l.mlRemaining * 1000)]));
  const drawOf = (l: InventoryLot) => Math.round(drawMlFor(l.containerType, l.mlPerContainer, doseMl) * 1000);
  let at = 0;
  return Array.from({ length: count }, () => {
    while (at < order.length && left.get(order[at].id)! < drawOf(order[at])) at += 1;
    const lot = order[Math.min(at, order.length - 1)];
    left.set(lot.id, left.get(lot.id)! - drawOf(lot));
    return { lot, short: at >= order.length };
  });
}
